import React, { useState } from 'react';
import { motion } from 'framer-motion';

const MotionDiv = motion.div;

const BlogPosts = () => {
  // Sample blog posts data
  const [posts] = useState([
    {
      id: 1,
      title: 'The Future of POS Systems in Singapore\'s F&B Industry',
      category: 'Industry Trends',
      date: 'December 12, 2024',
      readTime: '8 min read',
      author: 'POSlah Team',
      excerpt: 'Explore how modern POS systems are transforming Singapore\'s food and beverage landscape, from hawker centers to high-end restaurants.',
      content: 'The F&B industry in Singapore is rapidly evolving, with technology playing a crucial role in streamlining operations and enhancing customer experiences. Modern POS systems are no longer just cash registers - they\'re comprehensive business management tools that handle everything from inventory to customer analytics.',
      image: '📊',
      tags: ['POS Systems', 'F&B', 'Singapore', 'Technology'],
      featured: true
    },
    {
      id: 2,
      title: 'Why Small Businesses Need Cloud-Based POS Solutions',
      category: 'Business Solutions',
      date: 'December 8, 2024',
      readTime: '6 min read',
      author: 'Sarah Lim',
      excerpt: 'Discover the advantages of cloud-based POS systems for small businesses, including real-time data access, automatic updates, and enhanced security.',
      content: 'Small businesses are increasingly turning to cloud-based POS solutions to stay competitive. These systems offer scalability, remote access, and cost-effectiveness that traditional systems can\'t match.',
      image: '☁️',
      tags: ['Cloud POS', 'Small Business', 'Technology'],
      featured: false
    },
    {
      id: 3,
      title: 'Inventory Management: The Hidden Key to F&B Success',
      category: 'Operations',
      date: 'December 5, 2024',
      readTime: '7 min read',
      author: 'Michael Tan',
      excerpt: 'Learn how proper inventory management through modern POS systems can reduce waste, improve profitability, and streamline operations.',
      content: 'Effective inventory management is often overlooked but crucial for F&B success. Modern POS systems with integrated inventory tracking can help businesses reduce waste by up to 30%.',
      image: '📦',
      tags: ['Inventory', 'F&B Management', 'Efficiency'],
      featured: false
    },
    {
      id: 4,
      title: '2024 Market Analysis: POS Technology Adoption in Southeast Asia',
      category: 'Market Research',
      date: 'November 30, 2024',
      readTime: '12 min read',
      author: 'Data Analytics Team',
      excerpt: 'Comprehensive analysis of POS technology adoption rates, market trends, and growth projections across Southeast Asian markets.',
      content: 'Our latest market research reveals significant growth in POS technology adoption across Southeast Asia, with Singapore leading the charge in modernization.',
      image: '📈',
      tags: ['Market Analysis', 'Southeast Asia', 'Growth'],
      featured: false
    },
    {
      id: 5,
      title: 'Digital Payments Revolution: What F&B Owners Need to Know',
      category: 'Payment Technology',
      date: 'November 25, 2024',
      readTime: '5 min read',
      author: 'Finance Team',
      excerpt: 'Understanding the shift towards digital payments and how modern POS systems can help businesses adapt to changing customer preferences.',
      content: 'The digital payment landscape is evolving rapidly. Businesses that don\'t adapt risk losing customers who prefer cashless transactions.',
      image: '💳',
      tags: ['Digital Payments', 'Customer Experience', 'Technology'],
      featured: false
    },
    {
      id: 6,
      title: 'Maximizing Efficiency: POS Analytics for Business Growth',
      category: 'Analytics',
      date: 'November 20, 2024',
      readTime: '9 min read',
      author: 'Business Intelligence Team',
      excerpt: 'How to leverage POS analytics to identify trends, optimize menu items, and drive business growth through data-driven decisions.',
      content: 'Data is the new oil in the F&B industry. Learn how to turn your POS data into actionable insights for business growth.',
      image: '📊',
      tags: ['Analytics', 'Business Growth', 'Data'],
      featured: false
    }
  ]);

  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Industry Trends', 'Business Solutions', 'Operations', 'Market Research', 'Payment Technology', 'Analytics'];

  const filteredPosts = selectedCategory === 'All' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const featuredPost = posts.find(post => post.featured);
  const regularPosts = posts.filter(post => !post.featured);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const staggerContainer = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="blog-posts">
      {/* Header */}
      <MotionDiv 
        className="blog-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="section-title">Industry Insights</h2>
        <p className="section-description">
          Expert insights on POS technology, market trends, and business strategies for the F&B industry
        </p>
      </MotionDiv>

      {/* Category Filter */}
      <MotionDiv 
        className="category-filter"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="filter-container">
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </MotionDiv>

      {/* Featured Post */}
      {featuredPost && selectedCategory === 'All' && (
        <MotionDiv 
          className="featured-post"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="featured-post-content">
            <div className="featured-badge">Featured Article</div>
            <div className="post-meta">
              <span className="post-category">{featuredPost.category}</span>
              <span className="post-date">{formatDate(featuredPost.date)}</span>
              <span className="read-time">{featuredPost.readTime}</span>
            </div>
            <h3 className="featured-post-title">{featuredPost.title}</h3>
            <p className="featured-post-excerpt">{featuredPost.excerpt}</p>
            <div className="post-tags">
              {featuredPost.tags.map((tag, index) => (
                <span key={index} className="post-tag">{tag}</span>
              ))}
            </div>
            <button 
              className="read-article-btn"
              onClick={() => setSelectedPost(featuredPost)}
            >
              Read Full Article
              <span className="btn-arrow">→</span>
            </button>
          </div>
          <div className="featured-post-visual">
            <div className="post-icon-container">
              <span className="post-icon">{featuredPost.image}</span>
            </div>
          </div>
        </MotionDiv>
      )}

      {/* Blog Posts Grid */}
      <MotionDiv 
        className="blog-grid"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        {(selectedCategory === 'All' ? regularPosts : filteredPosts.filter(post => !post.featured))
          .map((post) => (
          <MotionDiv
            key={post.id}
            className="blog-card"
            variants={cardVariants}
            whileHover={{ 
              y: -8, 
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)" 
            }}
            onClick={() => setSelectedPost(post)}
          >
            <div className="blog-card-header">
              <div className="post-icon-small">{post.image}</div>
              <div className="post-meta-small">
                <span className="post-category small">{post.category}</span>
                <span className="post-date small">{formatDate(post.date)}</span>
              </div>
            </div>
            
            <h4 className="blog-card-title">{post.title}</h4>
            <p className="blog-card-excerpt">{post.excerpt}</p>
            
            <div className="blog-card-footer">
              <div className="post-author">
                <span>By {post.author}</span>
                <span className="read-time">{post.readTime}</span>
              </div>
              <div className="post-tags-small">
                {post.tags.slice(0, 2).map((tag, index) => (
                  <span key={index} className="post-tag small">{tag}</span>
                ))}
              </div>
            </div>
          </MotionDiv>
        ))}
      </MotionDiv>

      {/* Blog Post Detail Modal */}
      {selectedPost && (
        <MotionDiv 
          className="blog-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setSelectedPost(null)}
        >
          <MotionDiv 
            className="blog-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-close"
              onClick={() => setSelectedPost(null)}
            >
              ✕
            </button>
            
            <div className="blog-modal-header">
              {selectedPost.featured && (
                <div className="featured-badge">Featured Article</div>
              )}
              <div className="modal-icon">{selectedPost.image}</div>
              <div className="modal-meta">
                <span className="post-category">{selectedPost.category}</span>
                <span className="post-date">{formatDate(selectedPost.date)}</span>
                <span className="read-time">{selectedPost.readTime}</span>
              </div>
              <h3 className="blog-modal-title">{selectedPost.title}</h3>
              <p className="modal-author">By {selectedPost.author}</p>
            </div>
            
            <div className="blog-modal-content">
              <p className="modal-excerpt">{selectedPost.excerpt}</p>
              <div className="modal-divider"></div>
              <div className="modal-full-content">
                <p>{selectedPost.content}</p>
              </div>
              <div className="modal-tags">
                {selectedPost.tags.map((tag, index) => (
                  <span key={index} className="post-tag">{tag}</span>
                ))}
              </div>
            </div>
            
            <div className="blog-modal-footer">
              <button 
                className="close-modal-btn"
                onClick={() => setSelectedPost(null)}
              >
                Close Article
              </button>
            </div>
          </MotionDiv>
        </MotionDiv>
      )}
    </div>
  );
};

export default BlogPosts;